import React, {
  forwardRef,
  useCallback,
  useImperativeHandle,
  useState,
} from "react";
import { Alert, Button, Modal } from "antd";
import { DialogRef } from "@/types/common";
import { useGlobalContext } from "@/hooks/context";
import { deleteScanOrderShippingLabels } from "@/apis/tms/order";

interface OpenDialogProps {
  orderId: string;
}

interface DeleteShippingLabelsDialogProps {
  className?: string;
  onDeleted?: () => void;
}

const DeleteShippingLabelsDialog = forwardRef<
  DialogRef,
  DeleteShippingLabelsDialogProps
>((props, ref) => {
  const { className, onDeleted } = props;
  const { hintPrompt } = useGlobalContext();
  const [open, setOpen] = useState(false);
  const [orderId, setOrderId] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const closeDialog = useCallback(() => {
    setOpen(false);
    setOrderId(null);
  }, []);

  const openDialog = useCallback((payload: unknown) => {
    const { orderId: id } = payload as OpenDialogProps;
    setOrderId(id);
    setOpen(true);
  }, []);

  const handleConfirm = useCallback(() => {
    if (!orderId) return;
    setLoading(true);
    deleteScanOrderShippingLabels(orderId)
      .then(() => {
        hintPrompt("success", "Shipping labels deleted");
        closeDialog();
        onDeleted?.();
      })
      .catch((err) => {
        hintPrompt(
          "error",
          "Failed to delete shipping labels",
          err?.msg ?? "Unknown error",
        );
      })
      .finally(() => {
        setLoading(false);
      });
  }, [orderId, hintPrompt, closeDialog, onDeleted]);

  useImperativeHandle(ref, () => ({
    showModal: openDialog,
    hideModal: closeDialog,
  }));

  return (
    <Modal
      className={className}
      title="Delete shipping labels, continue?"
      open={open}
      closeIcon={false}
      onCancel={closeDialog}
      footer={
        <div className="flex flex-row justify-end gap-2">
          <Button onClick={closeDialog} disabled={loading}>Cancel</Button>
          <Button
            type="primary"
            danger
            loading={loading}
            onClick={handleConfirm}
          >
            Delete
          </Button>
        </div>
      }
      maskClosable={false}
    >
      <Alert
        type="warning"
        showIcon
        description={
          <div>
            <p>This action cannot be undone.</p>
            <p>
              It may fail to delete the shipping labels. You may need to try
              multiple times.
            </p>
          </div>
        }
      />
    </Modal>
  );
});

DeleteShippingLabelsDialog.displayName = "DeleteShippingLabelsDialog";

export default DeleteShippingLabelsDialog;
